import { useToast } from '@/use/toast';
import type { Customer } from '@/types/user';
import { tGlobal } from '@/i18n';
import { couponProducts, scopedCouponsFrom, type ScopedCoupon } from '@/lib/couponPlans';
import { isNetworkError } from '@/lib/isNetworkError';
import { isSupportedCurrency } from '@/lib/currencies';
import { logError } from '@/lib/logError';
import rest from '@/lib/rest';
import { stripePaymentMethodId } from '@/lib/stripeBilling';
import {
  billingPeriodMonths,
  carryRefusal,
  carryRefusalKey,
  collectDiscountEntries,
  couponRefusal,
  lifetimeGiftKey,
  placeCoupon,
  resolveCouponFromDiscountRow,
  type DiscountRow,
} from '@/lib/stripeDiscounts';
import { toltReferralPayload } from '@/lib/tolt';
import { useUserStore } from '@/stores/user';
import type {
  BillingAddress,
  Plan as BillingPlan,
  CurrencySwitchPreview,
  Invoice,
  OfferingProducts,
  Offerings as OfferingsData,
  PaymentMethod,
  PlanData,
  PlanProduct,
  Subscription,
} from '@/types/billing';
import { RestError } from '@dabble/rest-client';
import { defineStore } from 'pinia';
import type { Stripe } from 'stripe';
import { computed, onScopeDispose, ref, watch, type ComputedRef } from 'vue';
// Assume we have API functions similar to the Svelte rest calls
// import { fetchBillingDataApi, fetchOfferingsApi } from '@/api/billing';
// For now, we'll use placeholders

// Define interfaces based on Stripe types (or simplified versions).
// Extend with other relevant customer fields when needed.
type StripeCustomer = Stripe.Customer;

const TERMINAL_SUB_STATUSES = new Set(['canceled', 'incomplete_expired']);

// Accept both the flat `{ products }` payload and the legacy `{ plans: { plans } }`.
export function normalizeOfferings(raw: unknown): OfferingsData | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as { products?: OfferingProducts; plans?: { plans?: OfferingProducts }; currencies?: string[] };
  const products = data.products ?? data.plans?.plans;
  return products ? { products, currencies: data.currencies } : null;
}

/** Product ids + interval price ids present in an offering, for plan-membership checks. */
function offeringMembership(products: OfferingProducts | null | undefined): {
  productIds: Set<string>;
  priceIds: Set<string>;
} {
  const productIds = new Set<string>();
  const priceIds = new Set<string>();
  for (const [productId, product] of Object.entries(products ?? {})) {
    productIds.add(productId);
    for (const interval of ['month', 'year', 'one-time'] as const) {
      const id = product[interval]?.id;
      if (id) priceIds.add(id);
    }
  }
  return { productIds, priceIds };
}

const PRIMARY_SUB_PRIORITY: Subscription['status'][] = [
  'active',
  'trialing',
  'past_due',
  'paused',
  'unpaid',
  'incomplete',
];

/**
 * The currency a subscription is billed in. Not `plan.currency`: that's the price's BASE currency
 * (USD), which a multi-currency price keeps even while the subscription bills in AUD (DAB-1996).
 *
 * Trusted only from a dabble-rest that compares checkouts against `sub.currency` too — the one that
 * sends `plan_amount`. An older server reads the price's base currency, so telling it 'aud' for an
 * AUD sub reads as a switch and cancels and recreates the sub; until it's live, keep its answer.
 */
export function billedCurrency(sub: Subscription | null | undefined): string | null {
  if (sub && 'plan_amount' in sub && sub.currency) return sub.currency;
  return sub?.plan?.currency ?? null;
}

/** Prefer a billable subscription when Stripe returns multiple rows (e.g. newest canceled + older active). */
export function pickPrimarySubscription(subs: Subscription[]): Subscription | null {
  if (!subs?.length) return null;
  const candidates = subs.filter((s) => !TERMINAL_SUB_STATUSES.has(s.status as string));
  if (!candidates.length) return null;
  for (const status of PRIMARY_SUB_PRIORITY) {
    const found = candidates.find((s) => s.status === status);
    if (found) return found;
  }
  return candidates[0];
}

/**
 * The current period's end, in Unix seconds. Read from the first line item first — that is the
 * field dabble-rest's cancelSubscription reads, and where the period lives in the API version
 * this codebase targets — with the top-level field as a fallback for older payload shapes.
 * `undefined` when neither is present; callers must treat that as "unknown", not "expired".
 */
export function resolveSubscriptionPeriodEnd(sub: Subscription): number | undefined {
  const raw = sub as unknown as { items?: { data?: Array<{ current_period_end?: number | null }> } };
  const fromItem = raw.items?.data?.[0]?.current_period_end;
  if (typeof fromItem === 'number') return fromItem;
  return typeof sub.current_period_end === 'number' ? sub.current_period_end : undefined;
}

/** Top-level `plan` can be absent on item/price-based subscriptions; derive from first line item. */
export function resolveSubscriptionPlan(sub: Subscription): Subscription['plan'] | undefined {
  const existing = sub.plan;
  if (existing && (existing as { id?: string }).id) return existing;
  const raw = sub as unknown as {
    items?: {
      data?: Array<{
        plan?: Subscription['plan'];
        price?: Stripe.Price & { product?: Stripe.Product | string };
      }>;
    };
  };
  const item = raw.items?.data?.[0];
  if (item?.plan) return item.plan;
  const price = item?.price;
  if (price && typeof price === 'object') {
    const product = price.product;
    const productObj =
      typeof product === 'object' && product && 'id' in product ? (product as Stripe.Product) : undefined;
    return {
      id: price.id,
      name: productObj?.name ?? '',
      amount: price.unit_amount,
      currency: price.currency ?? 'usd',
      interval: (price.recurring?.interval as string) ?? 'month',
      product: productObj
        ? { id: productObj.id, name: productObj.name }
        : { id: typeof product === 'string' ? product : price.id },
      description: productObj?.description ?? undefined,
    } as Subscription['plan'];
  }
  return existing;
}

/**
 * What `POST /billing/coupons/:code` resolves a display code to: the Stripe
 * promotion-code id the PUT/activate paths accept, plus the coupon it carries.
 * `coupon` is null when the server sent an unexpanded reference.
 */
export interface ValidatedCoupon {
  id: string;
  coupon: Stripe.Coupon | null;
  /** Set on a Lifetime gift card: the Lifetime price it pays for, which its checkout must buy. */
  lifetimePrice?: LifetimeGiftPrice;
}

export interface LifetimeGiftPrice {
  id: string;
  product: string;
}

export type CouponInterval = 'month' | 'year' | 'lifetime';

/** The validate response is a PromotionCode with `promotion.coupon` expanded; older
 * shapes carried the coupon at the top level. Either way the id is what gets applied. */
function resolveValidatedCoupon(raw: unknown): ValidatedCoupon | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { id?: unknown; coupon?: unknown; promotion?: { coupon?: unknown }; lifetimePrice?: unknown };
  if (typeof r.id !== 'string' || !r.id) return null;
  const candidate = r.promotion?.coupon ?? r.coupon;
  const coupon = candidate && typeof candidate === 'object' ? (candidate as Stripe.Coupon) : null;
  const gift = r.lifetimePrice as Partial<LifetimeGiftPrice> | undefined;
  const lifetimePrice =
    typeof gift?.id === 'string' && typeof gift.product === 'string' ? { id: gift.id, product: gift.product } : null;
  return { id: r.id, coupon, ...(lifetimePrice ? { lifetimePrice } : {}) };
}

/** The API code a billing failure carries (`COUPON_EXPIRED`, …), or `UNKNOWN`. */
function restErrorCode(err: unknown): string {
  return err instanceof RestError ? err.message : err instanceof Error ? err.message : 'UNKNOWN';
}

const CHECKOUT_COUPON_KEY = 'dabble:billing:checkoutCoupon';
/** A coupon waiting for checkout; `products` is set when it only works on some plans. */
interface CheckoutCoupon {
  id: string;
  display: string;
  products?: string[];
  /** Its terms, to judge it against the plan being bought and name it in the order summary. */
  coupon?: Stripe.Coupon;
  /** A Lifetime gift card's price: the only plan it can be redeemed for. */
  lifetimePrice?: LifetimeGiftPrice;
}

/** Read the checkout coupon stashed in sessionStorage — survives a 3DS redirect that
 * reloads the store mid-checkout (RyanHavoc review). */
function readStashedCheckoutCoupon(): CheckoutCoupon | null {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_COUPON_KEY);
    return raw ? (JSON.parse(raw) as CheckoutCoupon) : null;
  } catch {
    return null;
  }
}

export const useBillingStore = defineStore('billing', () => {
  // --- State ---
  const { showToast } = useToast();

  const customer = ref<StripeCustomer | null>(null);
  const subscriptions = ref<Subscription[]>([]);
  const paymentMethods = ref<PaymentMethod[]>([]);
  const plans = ref<BillingPlan[]>([]);
  const invoices = ref<Invoice[]>([]);
  const upcomingInvoice = ref<Stripe.Invoice | null>(null);
  const selectedPlan = ref<PlanData | null>(null);
  const selectedPlanId = ref<string | null>(null);
  // A coupon validated at checkout by a customer with no live subscription to attach
  // it to. Held here — and mirrored to sessionStorage so a 3DS redirect mid-checkout
  // doesn't drop it — so the invoice preview and POST /billing/checkout/activate can
  // thread it into the subscription-create call (DAB-848). Cleared once activated.
  const checkoutCoupon = ref<CheckoutCoupon | null>(readStashedCheckoutCoupon());
  const offerings = ref<OfferingsData | null>(null);
  // The CURRENT offering's products, captured separately from `offerings` — which in
  // offer-link mode holds an older support offering. The currency gate must judge a
  // subscriber's plan against the current catalog, never the offer catalog (DAB-849).
  const currentOfferingProducts = ref<OfferingProducts | null>(null);
  // When set, the pricing grid is showing an older offering unlocked by a signed
  // support link (not the current catalog). Keeps currency switches + plan
  // selection from reverting to the current offering.
  const offerToken = ref<string | null>(null);
  const hasTrialAvailable = ref<boolean>(false);
  // The lifetime upgrade dabble-rest offers this customer, priced in the currency their carrier
  // bills in. Set only for an owner it deems eligible: the one signal the client trusts.
  const lifetimeUpgrade = ref<PlanData | null>(null);
  // Retrial cooldown length in months, surfaced from dabble-rest (Pup's live
  // `global/settings.canHaveTrial`, default 4).
  const canHaveTrialMonths = ref<number>(4);
  const isLoading = ref<boolean>(false);
  const selection = ref<'month' | 'year' | 'lifetime'>('month');
  const product = ref<PlanProduct | null>(null);
  const error = ref<string | null>(null); // Store error messages as strings
  const lastApplyError = ref<string | null>(null);
  // The API code behind `lastApplyError` (`COUPON_INVALID`, `COUPON_EXPIRED`, …). The localized
  // string is what a person reads; this is what telemetry records, since a chart can't be broken
  // down by a translated sentence (DAB-1303).
  const lastApplyErrorCode = ref<string | null>(null);
  const userStore = useUserStore();

  // Display currency. Seeded from a prior explicit choice; otherwise undefined,
  // which lets the server resolve it from geo (cf-ipcountry) on the first fetch.
  const CURRENCY_KEY = 'dabble:billing:currency';
  const currency = ref<string | undefined>(localStorage.getItem(CURRENCY_KEY) || undefined);

  // --- Actions ---

  /**
   * Trial eligibility for gating the "Start Trial" CTA. Uses `GET /billing/trial`
   * (getTrialStatus) which — unlike `/billing/data` — resolves for users with no
   * Stripe customer yet, so brand-new users still get a correct answer. Advisory
   * only: on failure we fail closed (`hasTrialAvailable = false`, button hidden)
   * without surfacing a toast.
   */
  async function fetchTrialStatus(uid?: string) {
    try {
      const res = (await rest
        .get('/billing/trial')
        .header('x-uid', uid || userStore.user?.uid || '')
        .credentials('include')
        .send()) as { available: boolean; canceledAt: number | null; canHaveTrial?: number };
      hasTrialAvailable.value = Boolean(res.available);
      if (typeof res.canHaveTrial === 'number' && Number.isFinite(res.canHaveTrial) && res.canHaveTrial >= 0) {
        canHaveTrialMonths.value = res.canHaveTrial;
      }
    } catch (err) {
      hasTrialAvailable.value = false;
      if (!isNetworkError(err)) logError('Failed to fetch trial status:', err);
    }
  }

  async function fetchBillingOverview(uid?: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const billingData = (await rest
        .get(currency.value ? `/billing/data?currency=${currency.value}` : '/billing/data')
        .header('x-uid', uid || userStore.user?.uid || '')
        .credentials('include')
        .send()) as {
        customer: Stripe.Customer;
        subscriptions: Subscription[];
        paymentMethods: PaymentMethod[];
        invoices: Invoice[];
        offerings: unknown;
        hasTrialAvailable?: boolean;
        canHaveTrial?: number;
        plans?: BillingPlan[];
        lifetimeUpgrade?: PlanData | null;
      };

      userStore.setStripeId(billingData.customer.id);
      customer.value = billingData.customer as StripeCustomer;
      subscriptions.value = billingData.subscriptions;
      paymentMethods.value = billingData.paymentMethods as unknown as PaymentMethod[];
      invoices.value = billingData.invoices;
      if (Array.isArray(billingData.plans)) {
        plans.value = billingData.plans;
      }
      // In offer-link mode the grid is owned by the older offering; refresh
      // everything else but don't overwrite it with the current catalog.
      const currentOffering = normalizeOfferings(billingData.offerings);
      if (!offerToken.value) offerings.value = currentOffering;
      // Always track the current catalog for the currency gate, even in offer-link
      // mode where `offerings` above intentionally keeps the older offering.
      currentOfferingProducts.value = currentOffering?.products ?? null;
      hasTrialAvailable.value = Boolean(billingData.hasTrialAvailable);
      lifetimeUpgrade.value = billingData.lifetimeUpgrade ?? null;
      if (
        typeof billingData.canHaveTrial === 'number' &&
        Number.isFinite(billingData.canHaveTrial) &&
        billingData.canHaveTrial >= 0
      ) {
        canHaveTrialMonths.value = billingData.canHaveTrial;
      }
      const healed = syncResolvedCurrency();
      await repriceOfferForResolvedCurrency();
      // The heal moved the currency after the grid was already priced in the old one.
      // Offer-link mode is re-priced above; otherwise re-fetch only when the grid
      // currency now disagrees with the switcher, so the two can't strand (mirrors
      // repriceOfferForResolvedCurrency's invariant). Self-terminating: the refetch
      // prices the grid in the healed currency, so the guard is false the second time.
      if (healed && !offerToken.value && offeringCurrency() && offeringCurrency() !== currency.value) {
        await fetchBillingOverview(uid);
        return;
      }
    } catch (err) {
      // An expired/cleared session surfaces here as RestError(401) on the live
      // GET /billing/data — an expected unauthenticated state, not a reportable
      // error (DABBLE-ACCOUNTS-2-W). Treat it like the view-mount auth 401: drop
      // the dead session and redirect to login, rather than leaving the portal
      // silently empty (or, for action-triggered refreshes, a silent no-op with
      // no feedback). Don't report it to Sentry or flash a danger toast.
      if (err instanceof RestError && err.code === 401) {
        error.value = null;
        // Imported lazily: refreshUser pulls in @/router, and a static import
        // here would form an eval-time cycle (router → BillingPortalView →
        // billing → router) and force every billing-store test to provide a
        // full vue-router mock. The redirect is only needed on this 401 path.
        const { endExpiredSession } = await import('@/lib/refreshUser');
        await endExpiredSession();
        return;
      }
      const message =
        err instanceof RestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to fetch billing overview';
      // GET /billing/data legitimately 404s with NO_BILLING_ACCOUNT for users
      // with no Stripe customer — invite-role / trial=none signups (createCustomer
      // is never called for them), brand-new accounts before server-side
      // provisioning completes, and never-subscribed portal visits. The UI copes
      // (plans/portal render the offerings grid, no error toast), so this is an
      // expected handled state, not a reportable error (DABBLE-ACCOUNTS-2-G /
      // DABBLE-ACCOUNTS-2-A). Classify it before logError so the handled 404 never
      // reaches Sentry/PostHog — same pattern as the 401 (-2-W) and network (-2-1B)
      // branches.
      if (message.includes('NO_BILLING_ACCOUNT')) {
        error.value = null;
        subscriptions.value = [];
        customer.value = null;
        lifetimeUpgrade.value = null;
        // In offer-link mode the grid is owned by the older offering, so don't
        // fetch or overwrite it with the current catalog.
        if (!offerToken.value) {
          try {
            const current = normalizeOfferings(
              await rest
                .get(
                  currency.value
                    ? `/billing/offerings/current?currency=${currency.value}`
                    : '/billing/offerings/current',
                )
                .credentials('include')
                .send(),
            );
            offerings.value = current;
            currentOfferingProducts.value = current?.products ?? null;
            syncResolvedCurrency();
          } catch {
            // offerings unavailable, leave null
          }
        }
        return;
      }
      // A transient browser-level fetch failure (offline, dropped connection,
      // request cancelled by navigating away, extension-blocked) never received
      // an HTTP response and is not a first-party bug (DABBLE-ACCOUNTS-2-1B,
      // same class as -2-10 / -2-11). This is the user-facing main load, so we
      // still surface feedback below — but don't report the network blip to
      // Sentry. Genuine errors (RestError with a status, unexpected exceptions)
      // still report.
      if (!isNetworkError(err)) {
        logError('Failed to fetch billing overview:', err);
      }
      error.value = message;
      showToast('danger', message);
    } finally {
      isLoading.value = false;
    }
  }

  // Reconcile the display currency after an overview load. Two jobs:
  //  1. Heal: a subscriber whose plan can't switch currency (legacy/USD-only) is
  //     forced onto the currency they're actually billed in — even overriding an
  //     explicit, persisted choice — because a stale, non-billable display currency
  //     (e.g. AUD carried over while on a USD-only legacy plan) makes every plan/
  //     interval change look like an impossible currency switch and dead-ends
  //     checkout with "That currency isn't supported" (DAB-849). Self-heals an
  //     account already stuck in that state.
  //  2. Seed: otherwise, when the user has made no explicit choice, default a known
  //     subscriber to the currency their subscription is billed in (not geo, so
  //     travel/VPN doesn't make a plain plan change look like a switch); everyone
  //     else falls back to the geo-priced offerings. Only adopt a supported currency.
  // Returns true only when the heal branch actually changed the currency, so the
  // caller can re-price the grid it already fetched in the old currency.
  function syncResolvedCurrency(): boolean {
    if (!canSwitchCurrency.value) {
      const billed = billedCurrency(primarySubscription.value);
      if (isSupportedCurrency(billed)) {
        if (currency.value !== billed) {
          currency.value = billed;
          localStorage.setItem(CURRENCY_KEY, billed);
          return true;
        }
        return false;
      }
    }
    if (currency.value) return false;
    const sub = subscriptions.value.find((s) => s.status === 'active' || s.status === 'trialing');
    const resolved = billedCurrency(sub) ?? offeringCurrency();
    if (isSupportedCurrency(resolved)) currency.value = resolved;
    return false;
  }

  function offeringCurrency(): string | undefined {
    const products = offerings.value?.products;
    if (!products) return undefined;
    for (const product of Object.values(products)) {
      const resolved = product.month?.currency || product.year?.currency || product['one-time']?.currency;
      if (resolved) return resolved;
    }
    return undefined;
  }

  // In offer-link mode the grid is fetched before we know the user's currency, so
  // it lands at the server geo-default. Once `syncResolvedCurrency` adopts the
  // subscription's currency, re-price the offer grid so the switcher and grid can
  // never disagree (which would otherwise strand a subscriber on mismatched prices
  // with a no-op switcher). No-op when the grid is already in the chosen currency.
  async function repriceOfferForResolvedCurrency() {
    if (!offerToken.value || !currency.value) return;
    if (offeringCurrency() === currency.value) return;
    await fetchOfferingForToken(offerToken.value);
  }

  // Switch the displayed/checkout currency and re-fetch prices in it.
  async function setCurrency(next: string) {
    if (next === currency.value) return;
    const prev = currency.value;
    currency.value = next;
    localStorage.setItem(CURRENCY_KEY, next);
    // In offer-link mode, re-price the older offering rather than reverting to
    // the current catalog. If the re-price fails, roll the selection back so the
    // grid stays consistent with the honored pricing instead of silently sticking.
    if (offerToken.value) {
      const res = await fetchOfferingForToken(offerToken.value);
      if (!res.ok) {
        currency.value = prev;
        if (prev) localStorage.setItem(CURRENCY_KEY, prev);
        else localStorage.removeItem(CURRENCY_KEY);
        showToast('danger', tGlobal('billing_currency_switch_error'));
      }
    } else {
      await fetchBillingOverview();
    }
  }

  // Preview a currency switch at checkout: the new plan price, the unused-time
  // credit we'll grant (FX'd to the chosen currency), and the net charged now.
  // Drives the confirmation dialog before we mutate the subscription.
  async function previewCurrencySwitch(planId: string): Promise<CurrencySwitchPreview> {
    return (await rest
      .post('/billing/subscription/currency/preview')
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send({ planId, currency: currency.value ?? 'usd' })) as CurrencySwitchPreview;
  }

  async function retryInvoicePayment(invoiceId: string): Promise<Invoice | null> {
    try {
      const updated = (await rest
        .post(`/billing/invoices/${invoiceId}/pay`)
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send()) as Invoice;
      invoices.value = invoices.value.map((i) => (i.id === invoiceId ? updated : i));
      // Successful retry flips the subscription from past_due → active; refresh so UI clears.
      // A retry that started a bank debit comes back `open` + payment_processing (DAB-1690):
      // refresh that too, so the banner and the payment-method rows stop asking for a card.
      if (updated?.status === 'paid' || updated?.payment_processing) {
        await fetchBillingOverview();
      }
      // The callers only confirm a `paid` retry; a debit that started needs saying too.
      if (updated?.payment_processing) {
        showToast('info', tGlobal('billing_warning_payment_processing'));
      }
      return updated;
    } catch (err) {
      showBillingError(err);
      // The invoice was already being paid by a debit this page didn't know about — reload so it
      // shows Processing instead of offering the same refused retry again.
      if (err instanceof Error && err.message === 'PAYMENT_PROCESSING') {
        await fetchBillingOverview();
      }
      return null;
    }
  }

  async function fetchInvoices(after?: string): Promise<Invoice[]> {
    isLoading.value = true;
    error.value = null;
    let result: Invoice[] = [];
    try {
      let req = rest
        .get('/billing/invoices')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include');
      if (after) req = req.query('after', after);
      result = (await req.send()) as Invoice[];
      if (!after) invoices.value = result;
    } catch (err) {
      showBillingError(err);
    } finally {
      isLoading.value = false;
    }
    return result;
  }

  async function getCustomer(uid: string, stripeId?: string) {
    try {
      const response = await rest
        .get('/billing/data')
        .header('x-uid', uid || '')
        .query('stripeId', stripeId || '')
        .credentials('include')
        .send();
      return response.customer;
    } catch (err) {
      return null;
    }
  }

  /** POST /billing/customer only — no trial. Updates local `user.stripeId` on success. */
  async function createStripeCustomerOnly({
    uid,
    email,
    name,
  }: {
    uid: string;
    email: string;
    name: string;
  }): Promise<string | false> {
    isLoading.value = true;
    error.value = null;
    try {
      const payload: Partial<Customer> = { uid, email, name };
      const response = await rest
        .post('/billing/customer')
        .header('x-uid', uid || '')
        .credentials('include')
        .send(payload);
      const stripeId = response.stripeId as string;
      userStore.setStripeId(stripeId);
      return stripeId;
    } catch (err) {
      showBillingError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Creates Stripe customer and optionally starts the trial (`POST /billing/trial`).
   * @param withTrial When true (default), chains trial creation after customer creation.
   */
  async function createCustomer({
    uid,
    email,
    name,
    withTrial = true,
  }: {
    uid: string;
    email: string;
    name: string;
    withTrial?: boolean;
  }): Promise<string | false> {
    const stripeId = await createStripeCustomerOnly({ uid, email, name });
    if (!stripeId || !withTrial) return stripeId;

    isLoading.value = true;
    error.value = null;
    try {
      // The trial subscription is what converts to paid weeks later, so the
      // affiliate click id has to be stamped on it now.
      await rest
        .post('/billing/trial')
        .header('x-uid', uid || '')
        .credentials('include')
        .send(await toltReferralPayload());
      showToast('success', tGlobal('billing_trial_started'));
      return stripeId;
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'SUBSCRIPTION_EXISTS') return stripeId;

      handleTrialError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /** Existing Stripe customer, no trial yet — `POST /billing/trial` (server ensures customer + trial). */
  async function startTrialSubscription(): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      // Same affiliate stamp as createCustomer's trial start above.
      await rest
        .post('/billing/trial')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send(await toltReferralPayload());
      await fetchBillingOverview();
      showToast('success', tGlobal('billing_trial_started'));
      return true;
    } catch (err: unknown) {
      handleTrialError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function pauseBillingSubscription(resumeDateUnixSeconds: number): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .put('/billing/subscription/pause')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send({ paused: resumeDateUnixSeconds });
      await fetchBillingOverview();
      return true;
    } catch (err) {
      showBillingError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function unpauseBillingSubscription(): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .delete('/billing/subscription/pause')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      await fetchBillingOverview();
      return true;
    } catch (err) {
      showBillingError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /** Save the checkout billing address to the Stripe customer for tax. Best-effort. */
  async function saveBillingAddress(address: BillingAddress): Promise<boolean> {
    try {
      await rest
        .post('/billing/customer/address')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send({ address });
      // Keep the local customer current: only fetchBillingOverview used to refresh it, so
      // anything reading `customer.address` between a save and the next overview fetch —
      // the checkout's Address Element rebuilt after a failed activation, for one — saw
      // the address the customer had just typed as absent (DAB-1407 review).
      if (customer.value) {
        customer.value = {
          ...customer.value,
          address: {
            line1: address.line1,
            line2: address.line2 ?? null,
            city: address.city ?? null,
            state: address.state ?? null,
            postal_code: address.postal_code ?? null,
            country: address.country,
          },
        };
      }
      return true;
    } catch (err) {
      if (!isNetworkError(err)) logError('saveBillingAddress', err);
      return false;
    }
  }

  async function setDefaultPaymentMethod(paymentMethodId: string) {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .post(`/billing/payment-methods/${paymentMethodId}/default`)
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      await fetchBillingOverview();
      showToast('success', tGlobal('billing_payment_method_default_updated'));
    } catch (err) {
      showBillingError(err);
    } finally {
      isLoading.value = false;
    }
  }

  async function removePaymentMethod(paymentMethodId: string) {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .delete(`/billing/payment-methods/${paymentMethodId}`)
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      // Optimistic update or fetch
      paymentMethods.value = paymentMethods.value.filter((pm) => pm.id !== paymentMethodId);
      await fetchBillingOverview();
      showToast('success', tGlobal('billing_payment_method_removed'));
    } catch (err) {
      showBillingError(err);
    } finally {
      isLoading.value = false;
    }
  }

  // --- New Action: Create Subscription ---
  async function createSubscription(plan: BillingPlan, paymentMethodId?: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const payload: { plan: BillingPlan; paymentMethodId?: string } = { plan };
      if (paymentMethodId) {
        payload.paymentMethodId = paymentMethodId;
      }
      // Call the API to create the subscription
      const response = await rest
        .post('/billing/subscription')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send(payload);

      if (response) {
        // Add the new subscription to the local state
        subscriptions.value.push(response.subscription);
        // Refresh the billing overview for consistency
        await fetchBillingOverview();
        showToast('success', response.message || tGlobal('billing_subscription_created'));
        return true; // Indicate success
      } else {
        throw new Error(response.message || 'Failed to create subscription');
      }
    } catch (err) {
      showBillingError(err);
      return false; // Indicate failure
    } finally {
      isLoading.value = false;
    }
  }

  async function resumeSubscription(): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .put('/billing/subscription')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send({});
      await fetchBillingOverview();
      return true;
    } catch (err) {
      showBillingError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // --- Cancel Subscription ---
  async function cancelSubscription(
    options: {
      reason?: string;
      feedback?: string;
      otherReason?: string;
      missingFeatures?: string;
      alternativeName?: string;
      technicalIssues?: string;
      when?: 'now' | 'end_of_period';
    } = {},
  ) {
    isLoading.value = true;
    error.value = null;
    try {
      const { when, ...rest_ } = options;
      const response = await rest
        .delete('/billing/subscription')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send(when === 'now' ? { ...rest_, when: 'now' } : rest_);

      if (response?.success !== false) {
        await fetchBillingOverview();
        showToast('success', response?.message || tGlobal('billing_subscription_updated'));
        return true;
      }
      throw new Error(response?.message || 'Failed to cancel subscription');
    } catch (err) {
      showBillingError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // Actionable billing/Stripe error codes → i18n keys. Codes not listed here fall
  // back to the generic message; genuine 500 internals (NO_CURRENT_OFFERING,
  // MISSING_PRODUCT, SETUP_INTENT_NO_CLIENT_SECRET, …) are captured in Sentry
  // server-side, so leaving them generic client-side is intentional.
  const BILLING_ERROR_I18N: Record<string, string> = {
    // Coupons / referrals
    COUPON_INVALID: 'billing_error_COUPON_INVALID',
    COUPON_EXPIRED: 'billing_error_COUPON_EXPIRED',
    COUPON_UNREDEEMABLE: 'billing_error_COUPON_UNREDEEMABLE',
    COUPON_NOT_VALID: 'billing_error_COUPON_NOT_VALID',
    REFERRAL_NEW_ACCOUNTS_ONLY: 'billing_error_REFERRAL_NEW_ACCOUNTS_ONLY',
    COUPON_INVALID_MULTIPLE_PERCENT: 'billing_error_COUPON_INVALID_MULTIPLE_PERCENT',
    COUPON_ALREADY_APPLIED: 'billing_error_COUPON_ALREADY_APPLIED',
    COUPON_NOT_FOR_LIFETIME: 'billing_error_COUPON_NOT_FOR_LIFETIME',
    COUPON_NOT_FOR_ANNUAL: 'billing_error_COUPON_NOT_FOR_ANNUAL',
    COUPON_ANNUAL_AT_PAYMENT_ONLY: 'billing_error_COUPON_ANNUAL_AT_PAYMENT_ONLY',
    // Lifetime gift cards
    COUPON_LIFETIME_GIFT: 'billing_error_COUPON_LIFETIME_GIFT',
    GIFT_LIFETIME_OWNED: 'billing_error_GIFT_LIFETIME_OWNED',
    GIFT_REDEMPTION_IN_PROGRESS: 'billing_error_GIFT_REDEMPTION_IN_PROGRESS',
    LIFETIME_GIFT_AMOUNT_DUE: 'billing_error_LIFETIME_GIFT_AMOUNT_DUE',
    // Trial / subscription state
    TRIAL_UNAVAILABLE: 'billing_error_TRIAL_UNAVAILABLE',
    NO_TRIAL_AVAILABLE: 'billing_error_TRIAL_UNAVAILABLE',
    SUBSCRIPTION_EXISTS: 'billing_error_SUBSCRIPTION_EXISTS',
    NO_SUBSCRIPTION: 'billing_error_NO_SUBSCRIPTION',
    INVALID_PLAN: 'billing_error_INVALID_PLAN',
    // Lifetime holders' $0 carrier subscription refuses plan change / cancel /
    // pause / currency switch (dabble-rest #358) — only reachable via a stale
    // tab or old link, since the portal hides these actions for lifetime plans.
    LIFETIME_OWNED: 'billing_error_LIFETIME_OWNED',
    // Lifetime purchase and upgrade (DAB-2007): not an owner of the tier the upgrade is from, an
    // upgrade price no longer on offer (a sale that ended), or a lifetime already owned.
    LIFETIME_UPGRADE_NOT_ELIGIBLE: 'billing_error_LIFETIME_UPGRADE_NOT_ELIGIBLE',
    LIFETIME_UPGRADE_UNAVAILABLE: 'billing_error_LIFETIME_UPGRADE_UNAVAILABLE',
    ALREADY_OWNED: 'billing_error_ALREADY_OWNED',
    PAYMENT_NOT_COMPLETED: 'billing_error_PAYMENT_NOT_COMPLETED',
    // Checkout activation / completion (DAB-1584): the first payment is still pending
    // after the server's retries, or the subscription changed under the request.
    PAYMENT_INCOMPLETE: 'billing_error_PAYMENT_INCOMPLETE',
    SUBSCRIPTION_STATE_CHANGED: 'billing_error_SUBSCRIPTION_STATE_CHANGED',
    // Invoice retry (DAB-1690): the invoice is already being paid by a debit that is clearing.
    PAYMENT_PROCESSING: 'billing_error_PAYMENT_PROCESSING',
    // Pause / resume
    PAUSE_LOCKED: 'billing_pause_lock_error_locked',
    PAUSE_COOLDOWN: 'billing_pause_cooldown_error',
    PAUSE_PENDING_CANCEL: 'billing_error_PAUSE_PENDING_CANCEL',
    PAUSE_RESUME_DATE_IN_PAST: 'billing_error_PAUSE_RESUME_DATE_IN_PAST',
    NO_MONTHLY_PLAN_AVAILABLE: 'billing_error_NO_MONTHLY_PLAN_AVAILABLE',
    // Payment methods / address
    NO_PAYMENT_METHOD: 'billing_error_NO_PAYMENT_METHOD',
    ADDRESS_REQUIRED: 'billing_error_ADDRESS_REQUIRED',
    // Currency selection / switch
    CURRENCY_REQUIRED: 'billing_error_CURRENCY_REQUIRED',
    UNSUPPORTED_CURRENCY: 'billing_error_UNSUPPORTED_CURRENCY',
    CURRENCY_NOT_SUPPORTED: 'billing_error_UNSUPPORTED_CURRENCY',
    CURRENCY_NOT_SUPPORTED_BY_PRICE: 'billing_error_UNSUPPORTED_CURRENCY',
    CURRENCY_SWITCH_REQUIRES_ACTIVE: 'billing_error_CURRENCY_SWITCH_REQUIRES_ACTIVE',
    // Stripe card errors (surfaced verbatim as `err.code` by the server)
    card_declined: 'billing_error_card_declined',
    expired_card: 'billing_error_card_error',
    incorrect_cvc: 'billing_error_card_error',
    incorrect_number: 'billing_error_card_error',
    insufficient_funds: 'billing_error_card_error',
    processing_error: 'billing_error_card_error',
  };

  function translateBillingApiError(message: string, fallbackKey = 'billing_coupon_error_generic'): string {
    const i18nKey = BILLING_ERROR_I18N[message] ?? fallbackKey;
    return tGlobal(i18nKey);
  }

  /**
   * True when the map has a localized message for this API code. Callers that need to
   * tell a known code from an unknown one ask here rather than comparing
   * translateBillingApiError's output against the fallback's — two codes can render the
   * same copy, and a locale missing both keys renders them identically (DAB-957).
   */
  function hasBillingApiError(code: string): boolean {
    return Object.prototype.hasOwnProperty.call(BILLING_ERROR_I18N, code);
  }

  /**
   * A failed checkout preview or activation's copy for a known code, else null. A live subscriber's
   * codes go on the subscription before checkout, so yearly billing refusing one there is refusing a
   * discount already running on it (checkoutDiscountRefusal).
   */
  function translateCheckoutError(code: string): string | null {
    if (code === 'COUPON_NOT_FOR_ANNUAL' && primarySubscription.value) return tGlobal(checkoutDiscountRefusalKey.value);
    return hasBillingApiError(code) ? translateBillingApiError(code) : null;
  }

  /** Localizes a known billing error code via BILLING_ERROR_I18N, stores it, and toasts it. */
  function showBillingError(err: unknown, fallbackKey = 'billing_error_generic'): void {
    const message = translateBillingApiError(err instanceof Error ? err.message : '', fallbackKey);
    error.value = message;
    showToast('danger', message);
  }

  /** Localizes a failed trial-start error; trial-unavailable codes also hide the
   * Start Trial CTA, and SUBSCRIPTION_EXISTS resyncs the stale local state. */
  function handleTrialError(err: unknown): void {
    const code = err instanceof Error ? err.message : '';
    if (code === 'TRIAL_UNAVAILABLE' || code === 'NO_TRIAL_AVAILABLE') hasTrialAvailable.value = false;
    // Fire-and-forget before showBillingError: fetchBillingOverview clears error.value on entry.
    if (code === 'SUBSCRIPTION_EXISTS') void fetchBillingOverview();
    showBillingError(err, 'billing_trial_error_generic');
  }

  /**
   * Whether dabble-rest will refuse a coupon, recording why: none on a lifetime plan or purchase, and
   * on annual billing only what placeCoupon takes. A code goes onto a live subscription between
   * payments and, at checkout (`interval`), onto the first invoice of the plan being bought. A
   * Lifetime gift card goes on neither, so only a Lifetime plan already held refuses it.
   */
  function refusesCoupon(
    coupon: Stripe.Coupon | null,
    { silent = false, interval }: { silent?: boolean; interval?: CouponInterval } = {},
  ): boolean {
    const sub = primarySubscription.value;
    const refusal = lifetimeGiftKey(coupon)
      ? isLifetime.value && 'GIFT_LIFETIME_OWNED'
      : isLifetime.value || interval === 'lifetime'
        ? 'COUPON_NOT_FOR_LIFETIME'
        : coupon &&
          ((sub && couponRefusal(coupon, billingPeriodMonths(sub.plan), false)) ||
            (interval && couponRefusal(coupon, billingPeriodMonths({ interval }), true)));
    if (!refusal) return false;
    recordCouponError(refusal, silent, interval);
    return true;
  }

  /**
   * A coupon refusal's copy. At checkout a live subscriber's code goes onto the current plan before it
   * changes, so a yearly plan refusing a code on the way to monthly billing is not the code refusing
   * monthly: it can be added once the switch is done.
   */
  function couponErrorMessage(raw: string, interval?: CouponInterval): string {
    const yearlyToMonthly = interval === 'month' && billingPeriodMonths(primarySubscription.value?.plan) > 1;
    return yearlyToMonthly && (raw === 'COUPON_ANNUAL_AT_PAYMENT_ONLY' || raw === 'COUPON_NOT_FOR_ANNUAL')
      ? tGlobal('checkout_coupon_after_monthly_switch')
      : translateBillingApiError(raw);
  }

  /** Records a failed coupon apply/validate: localized copy for the UI, the raw code for telemetry. */
  function recordCouponError(raw: string, silent: boolean, interval?: CouponInterval): void {
    const msg = couponErrorMessage(raw, interval);
    error.value = msg;
    lastApplyError.value = msg;
    lastApplyErrorCode.value = raw;
    if (!silent) showToast('danger', msg);
  }

  /**
   * Validates a display code via `POST /billing/coupons/:code` without applying it.
   *
   * Resolves to the promotion code's id plus its expanded coupon (percent/amount off,
   * duration) so a caller can show what the code is worth before attaching it — the
   * "replace your current coupon?" step in BillingCouponLinkModal (DAB-1431). Resolves to
   * `null` on an invalid/expired code, with the localized reason in `lastApplyError`
   * and the API code in `lastApplyErrorCode`; the error toast is skipped with
   * `silent: true`.
   *
   * `keepLoading: true` is for `applyCouponCode`'s own internal call: it leaves
   * `isLoading` set on success so the flag doesn't dip `true → false → true` across
   * the await boundary between validating and attaching (RyanHavoc review) — a public
   * store flag a future `:disabled="isLoading"` binding could otherwise double-submit
   * through. Still cleared on a validation failure, since nothing follows it then.
   */
  async function validateCouponCode(
    displayCode: string,
    options: { silent?: boolean; keepLoading?: boolean } = {},
  ): Promise<ValidatedCoupon | null> {
    const trimmed = displayCode.trim();
    if (!trimmed) return null;
    const { silent = false, keepLoading = false } = options;

    isLoading.value = true;
    error.value = null;
    lastApplyError.value = null;
    lastApplyErrorCode.value = null;
    try {
      const validated = await rest
        .post(`/billing/coupons/${encodeURIComponent(trimmed)}`)
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      const resolved = resolveValidatedCoupon(validated);
      if (!resolved) {
        recordCouponError('COUPON_INVALID', silent);
        isLoading.value = false;
        return null;
      }
      if (!keepLoading) isLoading.value = false;
      return resolved;
    } catch (err) {
      recordCouponError(restErrorCode(err), silent);
      isLoading.value = false;
      return null;
    }
  }

  /**
   * Applies an already-validated coupon: attaches it to a live subscription via
   * `PUT /billing/subscription`, or stashes it for checkout when there's no
   * subscription yet. Skips the validate round trip that `applyCouponCode` does —
   * for a caller that already holds a `ValidatedCoupon` from its own
   * `validateCouponCode` call, such as BillingCouponLinkModal's "Apply new" step, which
   * validates once up front to describe the incoming coupon and would otherwise
   * validate the same code a second time before the PUT (RyanHavoc review).
   *
   * `displayCode` is the trimmed code as the user entered it — kept distinct from
   * `validated.id` (the Stripe promotion-code id) because it's what's shown back if
   * the coupon has to be stashed for checkout instead of applied immediately.
   *
   * `silent: true` suppresses the success/error toasts; `replace: true` asks the
   * server to drop the subscription's existing discounts in the same update
   * instead of stacking (DAB-1431). `productId` is the plan the customer is looking
   * at: a code that can't discount it gets a warning toast instead of a success one. `interval` is
   * the billing being checked out: a code it can't take is refused before anything is sent.
   *
   * A Lifetime gift card is never attached: it is held for checkout like a new subscriber's code,
   * and checkout moves onto the Lifetime plan it pays for (`checkoutGift`).
   */
  async function applyValidatedCoupon(
    displayCode: string,
    validated: ValidatedCoupon,
    options: { silent?: boolean; replace?: boolean; productId?: string; interval?: CouponInterval } = {},
  ): Promise<boolean> {
    const { silent = false, replace = false, productId } = options;
    const products = couponProducts(validated.coupon);
    const couponId = validated.id;

    if (refusesCoupon(validated.coupon, options)) {
      isLoading.value = false;
      return false;
    }

    isLoading.value = true;
    try {
      if (validated.lifetimePrice) {
        if (!(await holdGift(displayCode, validated.id, validated.coupon, validated.lifetimePrice))) {
          recordCouponError('COUPON_NOT_VALID', silent);
          return false;
        }
      } else if (primarySubscription.value) {
        // A live subscription exists — attach the discount to it directly.
        const body: { coupon: string; replaceDiscounts?: boolean } = { coupon: couponId };
        if (replace) body.replaceDiscounts = true;
        await rest
          .put('/billing/subscription')
          .header('x-uid', userStore.user?.uid || '')
          .credentials('include')
          .send(body);
        await fetchBillingOverview();
      } else {
        // No subscription to attach to (new subscriber at checkout). Stash the
        // validated coupon; the invoice preview and checkout activation thread it
        // into the subscription-create call. A coupon-only PUT would 500 (DAB-848).
        checkoutCoupon.value = {
          id: couponId,
          display: displayCode,
          ...(products ? { products } : {}),
          ...(validated.coupon ? { coupon: validated.coupon } : {}),
        };
        sessionStorage.setItem(CHECKOUT_COUPON_KEY, JSON.stringify(checkoutCoupon.value));
      }
      if (!silent) {
        if (validated.lifetimePrice) {
          showToast('success', tGlobal('billing_gift_applied'));
        } else if (products && productId && !products.includes(productId)) {
          showToast('warning', tGlobal('billing_coupon_applied_other_plan'));
        } else {
          showToast('success', tGlobal('billing_coupon_applied'));
        }
      }
      return true;
    } catch (err) {
      recordCouponError(restErrorCode(err), silent, options.interval);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Validates the display code via `POST /billing/coupons/:code`, then applies it.
   * A thin validate-then-apply wrapper over `validateCouponCode` + `applyValidatedCoupon`
   * for callers that only have the raw code (every existing caller: `BillingCouponApplyButton`,
   * `BillingCancelSubscriptionForm`, `ReferralApplyView`, `authFlow`'s referral-code
   * bootstrap). A caller that has already validated the code — BillingCouponLinkModal's
   * replace step — should call `applyValidatedCoupon` directly instead of validating twice.
   *
   * - With a live subscription, attaches it immediately via `PUT /billing/subscription`.
   *   `replace: true` asks the server to drop the subscription's existing discounts
   *   in the same update instead of stacking — the "Apply new" choice when a promo
   *   link meets a subscription that already has a coupon (DAB-1431).
   * - With no subscription (a new subscriber at checkout), there is nothing to attach
   *   to — a coupon-only PUT 500s (DAB-848) — so the validated coupon is stashed in
   *   `checkoutCoupon` and threaded into the invoice preview + checkout activation.
   *
   * `silent: true` suppresses the success/error toasts so callers can render their
   * own UI; the localized error string is still exposed via `lastApplyError`, and the
   * raw API code via `lastApplyErrorCode`.
   */
  async function applyCouponCode(
    displayCode: string,
    options: { silent?: boolean; replace?: boolean; productId?: string; interval?: CouponInterval } = {},
  ): Promise<boolean> {
    const trimmed = displayCode.trim();
    if (!trimmed) return false;
    const { silent = false } = options;
    if (refusesCoupon(null, { silent })) return false;

    const validated = await validateCouponCode(trimmed, { silent, keepLoading: true });
    if (!validated) return false;
    return applyValidatedCoupon(trimmed, validated, options);
  }

  /** Drop a coupon stashed for checkout (after activation, or when abandoning it). */
  function clearCheckoutCoupon() {
    checkoutCoupon.value = null;
    sessionStorage.removeItem(CHECKOUT_COUPON_KEY);
  }

  /**
   * Holds a Lifetime gift card for checkout and points checkout at the Lifetime plan it pays for.
   * False when that plan isn't on offer here.
   */
  async function holdGift(
    display: string,
    id: string,
    coupon: Stripe.Coupon | null,
    lifetimePrice: LifetimeGiftPrice,
  ): Promise<boolean> {
    if (!offerings.value) await fetchBillingOverview();
    const plan = [offerings.value?.products, currentOfferingProducts.value]
      .map((products) => products?.[lifetimePrice.product])
      .find((candidate) => candidate?.['one-time']?.id === lifetimePrice.id);
    if (!plan) return false;
    checkoutCoupon.value = { id, display, lifetimePrice, ...(coupon ? { coupon } : {}) };
    sessionStorage.setItem(CHECKOUT_COUPON_KEY, JSON.stringify(checkoutCoupon.value));
    return selectPlanForCheckout(plan, 'lifetime');
  }

  /** Point checkout at `plan`, keeping the given interval. False when the plan has no such price. */
  function selectPlanForCheckout(plan: PlanProduct, interval: CouponInterval): boolean {
    const price = plan[interval === 'lifetime' ? 'one-time' : interval];
    if (!price?.id) return false;
    product.value = plan;
    selectedPlan.value = price;
    selectedPlanId.value = price.id;
    selection.value = interval;
    return true;
  }

  /** Point checkout at the lifetime upgrade dabble-rest offers this owner. False when it offers none. */
  function selectLifetimeUpgrade(): boolean {
    const upgrade = lifetimeUpgrade.value;
    const target = upgrade?.product;
    if (!canUpgradeLifetime.value || !upgrade || !target) return false;
    product.value = currentOfferingProducts.value?.[target.id] ?? { id: target.id, name: target.name };
    selectedPlan.value = upgrade;
    selectedPlanId.value = upgrade.id;
    selection.value = 'lifetime';
    return true;
  }

  async function fetchPlan(planId: string) {
    const response = await rest
      .get(`/billing/plans/${planId}`)
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send();
    return response;
  }

  /** Preview of the next invoice (`GET /billing/invoices/upcoming`). Returns null if none or on error. */
  async function fetchUpcomingInvoice(): Promise<Stripe.Invoice | null> {
    const uid = userStore.user?.uid || '';
    try {
      const invoice = (await rest
        .get('/billing/invoices/upcoming')
        .header('x-uid', uid)
        .credentials('include')
        .send()) as Stripe.Invoice | null;
      const resolved = invoice?.id ? invoice : null;
      upcomingInvoice.value = resolved;
      return resolved;
    } catch (err) {
      // A transient fetch network failure (offline, dropped connection, request
      // cancelled by navigating away) on this non-critical preview is expected,
      // not a first-party bug — clear state and return null without reporting it
      // to Sentry (DABBLE-ACCOUNTS-2-10 / -2-11). Genuine errors still report.
      if (!isNetworkError(err)) {
        logError('fetchUpcomingInvoice', err);
      }
      upcomingInvoice.value = null;
      return null;
    }
  }

  /** The offering a given price belongs to — resolves grandfathered products the
   *  current `offerings` catalog omits. Returns null on error. */
  async function fetchOfferingForPrice(priceId: string): Promise<OfferingsData | null> {
    try {
      const path = currency.value
        ? `/billing/offerings/for-price/${priceId}?currency=${currency.value}`
        : `/billing/offerings/for-price/${priceId}`;
      const raw = await rest
        .get(path)
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      return normalizeOfferings(raw);
    } catch (err) {
      if (!isNetworkError(err)) logError('Failed to fetch offering for price:', err);
      return null;
    }
  }

  /**
   * Resolve a signed support "old pricing" link into the pricing grid. On
   * success the older offering replaces `offerings` and `offerToken` is set so
   * currency switches + plan selection keep it. On failure the reason lets the
   * page show a friendly message instead of a broken-looking error.
   */
  async function fetchOfferingForToken(
    token: string,
  ): Promise<{ ok: true } | { ok: false; reason: 'expired' | 'invalid' | 'wrong-account' | 'network' }> {
    try {
      const encoded = encodeURIComponent(token);
      const path = currency.value
        ? `/billing/offerings/link/${encoded}?currency=${currency.value}`
        : `/billing/offerings/link/${encoded}`;
      const raw = await rest
        .get(path)
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      offerings.value = normalizeOfferings(raw);
      offerToken.value = token;
      return { ok: true };
    } catch (err) {
      // Leave offerToken as-is: a transient re-price failure must not silently drop
      // offer mode (which would let the next plan click revert to current pricing).
      // A network blip is retryable, not a dead link. Only genuinely unexpected
      // errors (not an API RestError) are worth reporting.
      if (isNetworkError(err)) return { ok: false, reason: 'network' };
      const code = err instanceof RestError ? err.message : '';
      const reason = code === 'LINK_EXPIRED' ? 'expired' : code === 'LINK_NOT_YOURS' ? 'wrong-account' : 'invalid';
      if (!(err instanceof RestError)) logError('Failed to resolve offer link:', err);
      return { ok: false, reason };
    }
  }

  /** Drop offer-link mode and return to the current catalog. */
  function clearOfferToken() {
    offerToken.value = null;
  }
  // --- End New Action ---

  // --- Getters (Computed Properties) ---
  const hasActiveSubscription = computed(() => {
    return subscriptions.value && subscriptions.value.some((sub) => sub.status === 'active');
  });

  const defaultPaymentMethod = computed<Stripe.PaymentMethod | null>(() => {
    const c = customer.value as Stripe.Customer | null;
    const raw = c?.invoice_settings?.default_payment_method;
    if (raw == null) return null;
    if (typeof raw === 'object' && raw !== null && 'id' in raw) {
      return raw as Stripe.PaymentMethod;
    }
    const id = stripePaymentMethodId(raw);
    return id ? ({ id } as Stripe.PaymentMethod) : null;
  });

  // Stripe Tax needs country + postal to place the customer's jurisdiction. If we
  // already have those on file, checkout can skip collecting an address again.
  const hasBillingAddress = computed(() => {
    const a = (customer.value as Stripe.Customer | null)?.address;
    return !!(a?.country && a.postal_code);
  });

  const activeSubscription = computed(() => {
    return subscriptions.value.find((sub) => sub.status === 'active');
  });

  const primarySubscription = computed<Subscription | null>(() => {
    const sub = pickPrimarySubscription(subscriptions.value);
    if (!sub) return null;
    const plan = resolveSubscriptionPlan(sub);
    if (plan && plan !== sub.plan) {
      return { ...sub, plan };
    }
    return sub;
  });

  /** Plan-limited coupons on the account itself. */
  const accountScopedCoupons = computed<ScopedCoupon[]>(() =>
    scopedCouponsFrom([
      primarySubscription.value as Record<string, unknown> | null,
      customer.value as Record<string, unknown> | null,
    ]),
  );

  /** A Lifetime gift card waiting for checkout, or null. */
  const checkoutGift = computed(() => (checkoutCoupon.value?.lifetimePrice ? checkoutCoupon.value : null));

  /**
   * Why the plan being bought can't take the coupon waiting for checkout (placeCoupon), or null. A
   * Lifetime gift card takes only the Lifetime price it was bought for.
   */
  const checkoutCouponRefusal = computed(() => {
    const gift = checkoutGift.value?.lifetimePrice;
    if (gift) return gift.id === selectedPlan.value?.id ? null : 'COUPON_LIFETIME_GIFT';
    const coupon = checkoutCoupon.value?.coupon;
    return coupon && selection.value !== 'lifetime'
      ? couponRefusal(coupon, billingPeriodMonths(selectedPlan.value), true)
      : null;
  });

  /**
   * The stashed coupon this checkout judges: none on a plan that can't take it, and on a lifetime
   * purchase only the gift card that pays for it.
   */
  const judgedCheckoutCoupon = computed(() => {
    if (checkoutCouponRefusal.value) return null;
    return checkoutGift.value ?? (selection.value === 'lifetime' ? null : checkoutCoupon.value);
  });

  /**
   * The stashed coupon checkout applies: the judged one, unless it would be a line off a product it
   * isn't for, which dabble-rest refuses (the plan notice offers the plan it is for).
   */
  const effectiveCheckoutCoupon = computed(() => {
    const stashed = judgedCheckoutCoupon.value;
    const asLine =
      stashed?.coupon && placeCoupon(stashed.coupon, billingPeriodMonths(selectedPlan.value), true) === 'line';
    const elsewhere = stashed?.products?.length && product.value?.id && !stashed.products.includes(product.value.id);
    return asLine && elsewhere ? null : stashed;
  });

  /**
   * A discount running on the subscription that can't come along to the plan being bought (dabble-rest's
   * carryDiscounts). Moving onto yearly billing turns a coupon with an end into a line on the change's
   * invoice and keeps a forever percent, but a forever amount off, or a coupon with more than a year
   * left, can't go on yearly billing (carryRefusal).
   */
  const runningCarryRefusal = computed<Stripe.Coupon | null>(() => {
    const sub = primarySubscription.value;
    const period = billingPeriodMonths(selectedPlan.value);
    if (!sub || selection.value === 'lifetime' || period <= 1) return null;
    const trialing = sub.status === 'trialing';
    if (billingPeriodMonths(sub.plan) === period && !trialing) return null;
    const cycle = {
      start: sub.items?.data?.[0]?.current_period_start ?? sub.current_period_start ?? 0,
      months: billingPeriodMonths(sub.plan),
      trialing,
    };
    for (const entry of collectDiscountEntries(sub as unknown as DiscountRow)) {
      if (!entry || typeof entry !== 'object') continue;
      const row = entry as DiscountRow;
      const coupon = resolveCouponFromDiscountRow(row);
      const discount = { start: Number(row.start) || 0, end: row.end as number | null };
      if (coupon && carryRefusal(coupon, discount, period, { cycle, product: product.value?.id })) return coupon;
    }
    return null;
  });

  /**
   * A checkout that moves the subscription to another currency: a cancel and recreate in dabble-rest.
   * A trial is moved too, then ended and charged today like any other checkout. A lifetime purchase
   * never is: dabble-rest bills it in the currency the customer already pays in.
   */
  const checkoutSwitchesCurrency = computed(
    () =>
      selection.value !== 'lifetime' &&
      (isActivePaid.value || isTrialing.value) &&
      !!subscriptionCurrency.value &&
      !!currency.value &&
      subscriptionCurrency.value !== currency.value,
  );

  // A currency switch judges running coupons its own way (it drops what it can't carry, with the
  // customer's consent), so its own preview is what refuses one.
  const checkoutDiscountRefusal = computed(() => (checkoutSwitchesCurrency.value ? null : runningCarryRefusal.value));
  const checkoutDiscountRefusalKey = computed(() =>
    carryRefusalKey(runningCarryRefusal.value, primarySubscription.value?.status === 'trialing'),
  );

  /** Plan-limited coupons on the account, plus one waiting for checkout. */
  const scopedCoupons = computed<ScopedCoupon[]>(() => {
    const pending = judgedCheckoutCoupon.value;
    const coupons = pending?.products ? [{ label: pending.display, products: pending.products }] : [];
    return coupons.concat(accountScopedCoupons.value);
  });

  // A native pause: Stripe's own pause_collection (or a `paused` status). Distinct from the legacy
  // homebrew pause below because dabble-rest cancels them differently — a native pause with prepaid
  // time left cancels at period end, a legacy pause always cancels outright (its `trialing` status
  // short-circuits cancelSubscription's cancelNow before the period is consulted).
  const isNativePausedSubscription = computed(() => {
    const sub = primarySubscription.value;
    if (!sub) return false;
    return sub.status === 'paused' || Boolean(sub.pause_collection?.behavior);
  });

  // The pre-pause_collection pause: the sub is parked on a trial whose trial_end is the resume
  // date, and carries `metadata.is_paused_until`. Still live on the server and still detected here.
  const isLegacyPausedSubscription = computed(() => {
    const until = primarySubscription.value?.metadata?.is_paused_until;
    return until != null && until !== '';
  });

  const isPausedSubscription = computed(() => isNativePausedSubscription.value || isLegacyPausedSubscription.value);

  // Backend-stamped Unix-second gates on subscription metadata; missing/invalid = grandfathered, no gate.
  const nowSeconds = ref(Math.floor(Date.now() / 1000));

  const metadataTimestamp = (key: string) =>
    computed<number | null>(() => {
      const raw = primarySubscription.value?.metadata?.[key];
      if (raw == null || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    });
  const isFutureTimestamp = (until: ComputedRef<number | null>) =>
    computed(() => until.value != null && nowSeconds.value < until.value);

  // Self-service unpause is gated until this date.
  const pauseLockedUntil = metadataTimestamp('pause_locked_until');
  const isPauseLocked = isFutureTimestamp(pauseLockedUntil);
  // Re-pausing is blocked until this date (3 months after the previous pause's resume).
  const pauseCooldownUntil = metadataTimestamp('pause_cooldown_until');
  const isPauseCoolingDown = isFutureTimestamp(pauseCooldownUntil);

  // One wakeup at the nearest gate deadline flips the flags while the page stays open;
  // with no future gate stamped, no timer exists at all.
  const nextGateExpiry = computed(() => {
    const deadlines = [pauseLockedUntil.value, pauseCooldownUntil.value].filter(
      (until): until is number => until != null && until > nowSeconds.value,
    );
    return deadlines.length ? Math.min(...deadlines) : null;
  });
  const MAX_TIMEOUT_MS = 2 ** 31 - 1; // longer delays overflow setTimeout and fire immediately
  let gateTimer: ReturnType<typeof setTimeout> | undefined;
  function armGateTimer(until: number | null) {
    clearTimeout(gateTimer);
    if (until == null) return;
    gateTimer = setTimeout(
      () => {
        nowSeconds.value = Math.floor(Date.now() / 1000);
        if (nowSeconds.value < until) armGateTimer(until);
      },
      Math.min(Math.max(until * 1000 - Date.now(), 0), MAX_TIMEOUT_MS),
    );
  }
  watch(nextGateExpiry, armGateTimer, { immediate: true });
  onScopeDispose(() => clearTimeout(gateTimer));

  const isTrialing = computed(() => primarySubscription.value?.status === 'trialing' && !isPausedSubscription.value);

  const isActivePaid = computed(() => primarySubscription.value?.status === 'active' && !isPausedSubscription.value);

  // Lifetime plans are modeled as active $0 recurring subscriptions in Stripe.
  const isLifetime = computed(() => isActivePaid.value && primarySubscription.value?.plan?.amount === 0);

  const canUpgradeLifetime = computed(() => isLifetime.value && !!lifetimeUpgrade.value);

  /** Checkout is buying the upgrade on offer, the one purchase a lifetime holder may make. */
  const isLifetimeUpgradeCheckout = computed(
    () => canUpgradeLifetime.value && selectedPlanId.value === lifetimeUpgrade.value?.id,
  );

  const isDelinquent = computed(() => {
    const status = primarySubscription.value?.status;
    return status === 'past_due' || status === 'unpaid';
  });

  // An open invoice whose payment is still clearing (an ACH debit, DAB-1690) is not one to
  // retry: paying it again is refused, so nothing offers to.
  const latestOpenInvoice = computed<Invoice | null>(() => {
    return invoices.value.find((i) => i.status === 'open' && !i.payment_processing) ?? null;
  });

  // A bank debit is clearing the past-due balance and nothing else is left to pay — the case where
  // the page should reassure rather than ask for a card. With a second, failed invoice still open
  // the retry flow stays, so the banner and the Retry buttons never contradict each other.
  const hasPaymentInFlight = computed(() => invoices.value.some((i) => i.status === 'open' && i.payment_processing));
  const isPaymentProcessing = computed(() => hasPaymentInFlight.value && !latestOpenInvoice.value);

  const isSubscribedDuringTrial = computed(() => {
    const sub = primarySubscription.value;
    if (!sub) return false;
    if (sub.status !== 'trialing') return false;
    if (isPausedSubscription.value) return false;
    if (sub.cancel_at_period_end) return false;
    return defaultPaymentMethod.value !== null;
  });

  // Whether to offer the standalone "Apply coupon" control: whenever a subscription
  // exists for Stripe to attach the discount to — any non-terminal sub (active,
  // past_due, paused, …). Two cases apply their coupon at checkout instead: a
  // customer with NO subscription, where the PUT path 500s with nothing to attach
  // to (DAB-848), and an unconverted trial, whose subscription is set to cancel at
  // trial end — let it lapse and checkout mints a fresh subscription that does not
  // inherit the discount, so a coupon applied here is silently thrown away.
  // A lifetime plan never takes a coupon.
  const canApplyCouponToSubscription = computed(
    () =>
      primarySubscription.value != null && !isLifetime.value && !(isTrialing.value && !isSubscribedDuringTrial.value),
  );

  const hasStripeCustomer = computed(() => Boolean(userStore.user?.stripeId));

  // The currency the active subscription is actually billed in (vs `currency`,
  // the display/checkout selection). A mismatch is what makes checkout a switch.
  const subscriptionCurrency = computed(() => billedCurrency(primarySubscription.value));

  // Whether this account may choose a billing/display currency other than what it's
  // already on. With no ongoing subscription the user is about to pick a plan, so any
  // offered currency is fair game. An existing subscriber — in ANY ongoing state,
  // dunning and paused included (`primarySubscription` already excludes terminal
  // subs) — can only switch on a plan that's part of the CURRENT offering: modern
  // plans carry multi-currency prices, but legacy/grandfathered plans are USD-only,
  // so offering another currency dead-ends every plan/interval change at checkout
  // with "That currency isn't supported" (DAB-849). The plan is matched by product id
  // OR price id — item/price-based subs synthesize product.id = price.id, which never
  // keys into the offering. Judged against `currentOfferingProducts`, never
  // `offerings` (which in offer-link mode is an older catalog). Falls open until the
  // current offering is known, so a mid-load flash never disables the switcher.
  const canSwitchCurrency = computed(() => {
    const sub = primarySubscription.value;
    if (!sub) return true;
    const products = currentOfferingProducts.value;
    if (!products) return true;
    const { productIds, priceIds } = offeringMembership(products);
    const productId = sub.plan?.product?.id;
    const priceId = sub.plan?.id;
    return (!!productId && productIds.has(productId)) || (!!priceId && priceIds.has(priceId));
  });

  return {
    // State
    customer,
    subscriptions,
    paymentMethods,
    plans,
    invoices,
    upcomingInvoice,
    selectedPlan,
    selectedPlanId,
    checkoutCoupon,
    checkoutCouponRefusal,
    checkoutGift,
    checkoutDiscountRefusal,
    checkoutDiscountRefusalKey,
    checkoutSwitchesCurrency,
    effectiveCheckoutCoupon,
    offerings,
    currentOfferingProducts,
    offerToken,
    hasTrialAvailable,
    lifetimeUpgrade,
    canHaveTrialMonths,
    isLoading,
    error,
    lastApplyError,
    lastApplyErrorCode,
    currency,

    // Actions
    fetchTrialStatus,
    fetchBillingOverview,
    setCurrency,
    previewCurrencySwitch,
    setDefaultPaymentMethod,
    removePaymentMethod,
    fetchInvoices,
    retryInvoicePayment,
    createCustomer,
    createStripeCustomerOnly,
    saveBillingAddress,
    startTrialSubscription,
    pauseBillingSubscription,
    unpauseBillingSubscription,
    getCustomer,
    cancelSubscription,
    resumeSubscription,
    createSubscription,
    validateCouponCode,
    applyCouponCode,
    applyValidatedCoupon,
    refusesCoupon,
    clearCheckoutCoupon,
    selectPlanForCheckout,
    selectLifetimeUpgrade,
    fetchPlan,
    fetchUpcomingInvoice,
    fetchOfferingForPrice,
    fetchOfferingForToken,
    clearOfferToken,
    translateBillingApiError,
    hasBillingApiError,
    translateCheckoutError,

    // Getters
    hasActiveSubscription,
    defaultPaymentMethod,
    hasBillingAddress,
    activeSubscription,
    primarySubscription,
    accountScopedCoupons,
    scopedCoupons,
    isPausedSubscription,
    isNativePausedSubscription,
    isLegacyPausedSubscription,
    pauseLockedUntil,
    isPauseLocked,
    pauseCooldownUntil,
    isPauseCoolingDown,
    isTrialing,
    isActivePaid,
    canApplyCouponToSubscription,
    isDelinquent,
    latestOpenInvoice,
    isPaymentProcessing,
    hasPaymentInFlight,
    isSubscribedDuringTrial,
    isLifetime,
    canUpgradeLifetime,
    isLifetimeUpgradeCheckout,
    hasStripeCustomer,
    subscriptionCurrency,
    canSwitchCurrency,
    selection,
    product,
  };
});
